import NameSyncStatus from "../../../components/devices/NameSyncStatus";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import ReactPaginate from "react-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Loading from "../../../components/common/loading";
import ModalManager from "../../../components/common/modal/ModalManager";
import NotificationManager from "../../../utils/notificationManager";
import eventRefs from "../../../assets/eventRef";
import { apiErrorMessageFromError } from "../../../utils/apiErrorMessage";
import { isUnknownApiEndpointError } from "../../../utils/networkErrorUtils";
import {
  fetchNetworkStatusWithCache,
  readNetworkStatusCache,
  writeNetworkStatusCache,
} from "../../../utils/networkStatusCache";
import {
  DEFAULT_TIMEFRAME_KEY,
  TIMEFRAME_PRESETS,
  createRangeFromDuration,
  findTimeframePreset,
  getDefaultTimeRange,
} from "../../../utils/timeRange";
import {
  AppToolbar,
  AppToolbarField,
  AppToolbarGrid,
} from "../../../components/common/appShell";
import {
  buildAlertDetailsPath,
  buildDeviceAlertsPath,
} from "../../../utils/deviceAlertUtils";
import {
  buildInterfaceRows,
  getActiveUplinkSummaryFromConf,
  getWanPrioritySummary,
  normalizeInterfaceKey,
} from "../../../utils/networkInterfaces";

import "./deviceOverview.css";

const DEFAULT_LOG_LIMIT = 20;
const MAX_OVERVIEW_ACTIVE_ALERTS = 5;
const TIME_INTERVAL_BETWEEN_TRIGGERS_EVENT_CODE = 54;

const ALERT_NEW_THRESHOLD_MS = 15 * 60 * 1000;
const ALERT_PERSISTENT_THRESHOLD_MS = 60 * 60 * 1000;
const ALERT_STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const POWER_TREND_WINDOW_MS = 24 * 60 * 60 * 1000;
const POWER_TREND_BUCKET_MINUTES = 15;
const POWER_TREND_CHANGE_WARNING_THRESHOLD_PCT = 20;
const POWER_TREND_CHANGE_DANGER_THRESHOLD_PCT = 40;

const ALERT_STARTED_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const POWER_TREND_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const OVERVIEW_TIMEFRAME_OPTION_KEYS = new Set([
  "1h",
  "6h",
  "24h",
  "7d",
  "30d",
  "90d",
  "custom",
]);

const TIMEFRAME_OPTIONS = TIMEFRAME_PRESETS.filter((option) =>
  OVERVIEW_TIMEFRAME_OPTION_KEYS.has(option.key)
);

const getNetworkHealthTone = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (
    normalized.includes("active") ||
    normalized.includes("ready") ||
    normalized.includes("healthy") ||
    normalized.includes("connected") ||
    normalized.includes("registered") ||
    normalized.includes("enabled")
  ) {
    return "success";
  }
  if (
    normalized.includes("down") ||
    normalized.includes("not registered") ||
    normalized.includes("unhealthy") ||
    normalized.includes("unavailable") ||
    normalized.includes("degraded") ||
    normalized.includes("no route") ||
    normalized.includes("failed") ||
    normalized.includes("error")
  ) {
    return "danger";
  }
  return "neutral";
};

const renderNetworkSummaryValue = (summary) => {
  if (!summary?.label) {
    return "-";
  }
  const detail = summary.ipAddress || summary.detail || "";
  if (!detail) {
    return summary.label;
  }
  return (
    <span className="device-overview__kv-stack">
      <span>{summary.label}</span>
      <span className="device-overview__kv-note">{detail}</span>
    </span>
  );
};

const compactOverviewNetworkParts = (parts) =>
  parts
    .map((part) =>
      part === undefined || part === null ? "" : String(part).trim(),
    )
    .filter(Boolean)
    .join(" · ");

const getNetworkStatusDetailParts = (item) =>
  String(item?.statusDetail || "")
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean);

const formatOverviewAddressMode = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "dhcp") return "DHCP";
  if (normalized === "static") return "Static";
  return "";
};

const getDetailValue = (item, prefix) => {
  const normalizedPrefix = prefix.toLowerCase();
  const detail = getNetworkStatusDetailParts(item).find((part) =>
    part.toLowerCase().startsWith(`${normalizedPrefix} `),
  );
  return detail ? detail.slice(prefix.length).trim() : "";
};

const getOverviewSignal = (item) => {
  const signal = item?.signalStrength ?? item?.signalLevel;
  if (signal !== undefined && signal !== null && signal !== "") {
    const text = String(signal).trim();
    return text.toLowerCase().includes("dbm") ? text : `${text} dBm`;
  }
  return getNetworkStatusDetailParts(item).find((part) =>
    part.toLowerCase().includes("dbm"),
  ) || "";
};

const formatRegistrationStatus = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  if (normalized.toLowerCase().includes("registered")) return "Registered";
  return normalized;
};

const isLikelyCarrierDetail = (part) => {
  const normalized = String(part || "").toLowerCase();
  return Boolean(
    normalized &&
      !normalized.includes("dbm") &&
      !normalized.includes("registered") &&
      !normalized.includes("priority") &&
      !normalized.includes("last check") &&
      !normalized.includes("primary not active") &&
      !normalized.includes("route unknown") &&
      !normalized.includes("cellular uplink"),
  );
};

const getOverviewNetworkDetail = (item) => {
  const interfaceName = normalizeInterfaceKey(item?.interfaceName);
  const parts = getNetworkStatusDetailParts(item);

  if (interfaceName === "lte0") {
    return compactOverviewNetworkParts([
      item?.operator || parts.find(isLikelyCarrierDetail),
      getOverviewSignal(item),
      formatRegistrationStatus(item?.registrationStatus),
    ]) || "-";
  }

  if (interfaceName === "wlan0") {
    return compactOverviewNetworkParts([
      item?.ssid || getDetailValue(item, "SSID"),
      item?.ipAddress || getDetailValue(item, "IP"),
    ]) || "-";
  }

  const addressMode =
    formatOverviewAddressMode(item?.addressMode) ||
    formatOverviewAddressMode(parts.find((part) =>
      ["dhcp", "static"].includes(part.toLowerCase()),
    ));
  const monitoringDetail = parts.find((part) =>
    ["not health monitored", "not monitored"].includes(part.toLowerCase()),
  );

  return compactOverviewNetworkParts([
    item?.ipAddress || getDetailValue(item, "IP"),
    addressMode,
    monitoringDetail,
  ]) || item?.linkSignal || "-";
};

const RENAME_MODAL_OPTIONS = {
  preventOutsideClose: true,
  modalStyle: {
    width: "520px",
    maxWidth: "90vw",
  },
};

const ACTIVITY_SOURCE_FILTERS = [
  { key: "all", label: "All", icon: "history" },
  { key: "attention", label: "Needs attention", icon: "priority_high" },
  { key: "alerts", label: "Alerts", icon: "notifications" },
  { key: "activity", label: "Activity", icon: "manage_history" },
];

const ACTIVITY_TYPE_FILTERS = [
  { key: "all", label: "Any type" },
  { key: "connectivity", label: "Connectivity" },
  { key: "config", label: "Config changes" },
  { key: "outlets", label: "Outlet commands" },
  { key: "access", label: "Access sessions" },
];

const ACTIVITY_ACTIONS_BY_FILTER = {
  connectivity: ["device_disconnected", "device_connected"],
  alerts: [
    "event_created",
    "event_updated",
    "event_deleted",
    "event_create_queued",
    "event_update_queued",
    "event_delete_queued",
    "action_created",
    "action_updated",
    "action_deleted",
    "action_create_queued",
    "action_update_queued",
    "action_delete_queued",
  ],
  config: [
    "device_edited",
    "device_configuration_updated",
    "device_fw_update",
    "device_firmware_updated",
  ],
  outlets: ["outlet_switched", "outlet_edited"],
  access: [
    "device_paired",
    "device_unpaired",
    "cli_session_started",
    "cli_session_stopped",
  ],
};

const ACTIVITY_PRESENTATION_BY_ACTION = {
  device_paired: { category: "Access", icon: "login", tone: "neutral" },
  device_unpaired: { category: "Access", icon: "logout", tone: "warning" },
  device_connected: { category: "Connectivity", icon: "cloud_done", tone: "success" },
  device_disconnected: { category: "Connectivity", icon: "cloud_off", tone: "danger" },
  device_edited: { category: "Config", icon: "edit", tone: "neutral" },
  device_configuration_updated: { category: "Config", icon: "settings", tone: "neutral" },
  device_fw_update: { category: "Config", icon: "update", tone: "warning" },
  device_firmware_updated: { category: "Config", icon: "update", tone: "success" },
  outlet_switched: { category: "Outlet", icon: "outlet", tone: "neutral" },
  outlet_edited: { category: "Outlet", icon: "edit", tone: "neutral" },
  device_request_queued: { category: "Command", icon: "queue", tone: "warning" },
  device_request_completed: { category: "Command", icon: "send", tone: "success" },
  device_request_retry: { category: "Command", icon: "error", tone: "danger" },
  cli_session_started: { category: "Access", icon: "terminal", tone: "neutral" },
  cli_session_stopped: { category: "Access", icon: "terminal", tone: "neutral" },
  event_created: { category: "Alert Config", icon: "event_upcoming", tone: "neutral" },
  event_updated: { category: "Alert Config", icon: "edit_calendar", tone: "neutral" },
  event_deleted: { category: "Alert Config", icon: "delete", tone: "warning" },
  event_create_queued: { category: "Alert Config", icon: "schedule", tone: "warning" },
  event_update_queued: { category: "Alert Config", icon: "schedule", tone: "warning" },
  event_delete_queued: { category: "Alert Config", icon: "schedule", tone: "warning" },
  action_created: { category: "Alert Action", icon: "bolt", tone: "neutral" },
  action_updated: { category: "Alert Action", icon: "manage_history", tone: "neutral" },
  action_deleted: { category: "Alert Action", icon: "delete", tone: "warning" },
  action_create_queued: { category: "Alert Action", icon: "schedule", tone: "warning" },
  action_update_queued: { category: "Alert Action", icon: "schedule", tone: "warning" },
  action_delete_queued: { category: "Alert Action", icon: "schedule", tone: "warning" },
};

const ACTIVITY_ICON_CONFIG = {
  device_paired: { icon: "login", url: "/devices/connected/:resourceId/overview" },
  device_unpaired: { icon: "logout", url: "/devices/connected/:resourceId/overview" },
  device_disconnected: {
    icon: "cloud_off",
    url: "/devices/connected/:resourceId/overview",
  },
  device_connected: {
    icon: "cloud_done",
    url: "/devices/connected/:resourceId/overview",
  },
  device_edited: {
    icon: "edit",
    url: "/devices/connected/:resourceId/configuration/device",
  },
  device_firmware_updated: {
    icon: "update",
    url: "/devices/connected/:resourceId/configuration/firmware",
  },
  outlet_switched: {
    icon: "outlet",
    url: "/devices/connected/:enclosureSn/outlets/:resourceId",
  },
  outlet_edited: {
    icon: "edit",
    url: "/devices/connected/:enclosureSn/outlets/:resourceId",
  },
  device_fw_update: {
    icon: "update",
    url: "/devices/connected/:resourceId/configuration/firmware",
  },
  device_request_queued: {
    icon: "queue",
    url: "/devices/connected/:resourceId/overview",
  },
  device_request_completed: {
    icon: "send",
    url: "/devices/connected/:resourceId/overview",
  },
  device_request_retry: {
    icon: "error",
    url: "/devices/connected/:resourceId/overview",
  },
  device_configuration_updated: {
    icon: "settings",
    url: "/devices/connected/:resourceId/configuration",
  },
  cli_session_started: {
    icon: "terminal",
    url: "/devices/connected/:resourceId/diagnostics#shell-workspace",
  },
  cli_session_stopped: {
    icon: "terminal",
    url: "/devices/connected/:resourceId/diagnostics#shell-workspace",
  },
  event_create_queued: {
    icon: "schedule",
    url: "/devices/connected/:enclosureSn/alerts",
  },
  event_update_queued: {
    icon: "schedule",
    url: "/devices/connected/:enclosureSn/alerts",
  },
  event_delete_queued: {
    icon: "schedule",
    url: "/devices/connected/:enclosureSn/alerts",
  },
  event_created: {
    icon: "event_upcoming",
    url: "/devices/connected/:enclosureSn/alerts/:resourceId",
  },
  event_updated: {
    icon: "edit_calendar",
    url: "/devices/connected/:enclosureSn/alerts/:resourceId",
  },
  event_deleted: {
    icon: "delete",
    url: "/devices/connected/:enclosureSn/alerts/:resourceId",
  },
  action_create_queued: {
    icon: "schedule",
    url: "/devices/connected/:enclosureSn/alerts",
  },
  action_update_queued: {
    icon: "schedule",
    url: "/devices/connected/:enclosureSn/alerts",
  },
  action_delete_queued: {
    icon: "schedule",
    url: "/devices/connected/:enclosureSn/alerts",
  },
  action_created: { icon: "bolt", url: "/devices/connected/:enclosureSn/alerts" },
  action_updated: {
    icon: "manage_history",
    url: "/devices/connected/:enclosureSn/alerts",
  },
  action_deleted: { icon: "delete", url: "/devices/connected/:enclosureSn/alerts" },
};

const EVENT_NAME_MAP = new Map(
  (eventRefs || []).map((entry) => [
    String(entry.code),
    entry.name || `Event ${entry.code}`,
  ]),
);

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const SkeletonLine = ({ width = "100%", height = 12 }) => (
  <div className="skeleton-blank" style={{ width, height, margin: 0 }} />
);

const hasNumber = (value) =>
  value !== undefined &&
  value !== null &&
  value !== "" &&
  Number.isFinite(Number(value));

const formatOverviewNumber = (value, digits = 2) => {
  if (!hasNumber(value)) return "-";
  return Number(value)
    .toFixed(digits)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatOverviewTrendMetric = (value, unit, digits = 2) => {
  if (!hasNumber(value)) return "-";
  return `${formatOverviewNumber(value, digits)} ${unit}`;
};

const formatOverviewTrendPercent = (value) => {
  if (!hasNumber(value)) return "-";
  const number = Number(value);
  const sign = number > 0 ? "+" : "";
  return `${sign}${formatOverviewNumber(number, 1)}%`;
};

const formatOverviewTrendTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return POWER_TREND_TIME_FORMATTER.format(date);
  } catch (error) {
    return "";
  }
};

const formatOverviewCurrentRange = (rows = []) => {
  const values = rows
    .map((row) => row?.totalCurrentRms)
    .filter(hasNumber)
    .map(Number);

  if (!values.length) return "-";

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (Math.abs(max - min) < 0.005) {
    return `${formatOverviewNumber(max, 2)} A`;
  }
  return `${formatOverviewNumber(min, 2)}-${formatOverviewNumber(max, 2)} A`;
};

const getOverviewPeakValue = (rows = [], field, metaValue) => {
  if (hasNumber(metaValue)) return Number(metaValue);

  return rows.reduce((peak, row) => {
    const value = hasNumber(row?.[field]) ? Number(row[field]) : null;
    if (value === null) return peak;
    return peak === null ? value : Math.max(peak, value);
  }, null);
};

const buildOverviewTrendParams = (serial) => {
  const before = Date.now();
  const params = new URLSearchParams();
  params.set("enclosure_serial_number", String(serial));
  params.set("since", String(before - POWER_TREND_WINDOW_MS));
  params.set("before", String(before));
  params.set("bucketMinutes", String(POWER_TREND_BUCKET_MINUTES));
  return params.toString();
};

const normalizeTrendPayload = (payload, source) => ({
  source,
  rows: Array.isArray(payload?.data) ? payload.data : [],
  meta: payload?.meta || {},
});

const getTrendSourceLabel = (source) =>
  source === "outlet" ? "outlets" : "inlets";

const getTrendDetailPath = (serial, source) =>
  source === "outlet"
    ? `/devices/connected/${serial}/outlets`
    : `/devices/connected/${serial}/inlets`;

const getOverviewTrendStatus = ({ rows, meta, error }) => {
  if (error) {
    return {
      label: "Trend data unavailable",
      tone: "warning",
      detail: error,
    };
  }

  if (!rows.length) {
    return {
      label: "Insufficient trend data",
      tone: "neutral",
      detail: "No current history is available for the selected window.",
    };
  }

  const anomaly = meta?.anomaly || {};
  if (meta?.loadDropDetected) {
    return {
      label: "Current drop detected",
      tone: "danger",
      detail: "Current is materially lower than its earlier baseline.",
    };
  }

  if (anomaly?.state === "low_load") {
    return {
      label: "Below expected range",
      tone: anomaly?.severity === "critical" ? "danger" : "warning",
      detail: anomaly?.message || "Current is below the learned profile.",
    };
  }

  if (anomaly?.state === "high_load") {
    return {
      label: "Above expected range",
      tone: anomaly?.severity === "critical" ? "danger" : "warning",
      detail: anomaly?.message || "Current is above the learned profile.",
    };
  }

  if (anomaly?.state === "normal") {
    return {
      label: "Current normal",
      tone: "success",
      detail: anomaly?.message || "Current is within its expected range.",
    };
  }

  return {
    label: "Current trend available",
    tone: "success",
    detail: "Current history is available for this device.",
  };
};

const getOverviewTrendReportingText = (source, meta = {}) => {
  const latest = meta.latest || {};
  if (source === "outlet") {
    const reporting = hasNumber(latest.currentReadingCount)
      ? Number(latest.currentReadingCount)
      : latest.reportingOutletCount;
    const expected = meta.expectedOutletCount || latest.reportingOutletCount;
    if (hasNumber(reporting) && hasNumber(expected) && Number(expected) > 0) {
      return `${reporting}/${expected} outlets reporting current`;
    }
    return "Outlet current reporting";
  }

  const reporting = hasNumber(latest.currentReadingCount)
    ? Number(latest.currentReadingCount)
    : latest.reportingInletCount;
  const expected = meta.expectedInletCount || latest.reportingInletCount;
  if (hasNumber(reporting) && hasNumber(expected) && Number(expected) > 0) {
    return `${reporting}/${expected} inlets reporting current`;
  }
  return "Inlet current reporting";
};

const getOverviewTrendChangeTone = (meta = {}) => {
  if (meta?.loadDropDetected) return "danger";
  if (!hasNumber(meta?.currentChangePct)) return "";

  const absoluteChange = Math.abs(Number(meta.currentChangePct));
  if (absoluteChange >= POWER_TREND_CHANGE_DANGER_THRESHOLD_PCT) {
    return "danger";
  }
  if (absoluteChange >= POWER_TREND_CHANGE_WARNING_THRESHOLD_PCT) {
    return "warning";
  }
  return "";
};

const buildOverviewTrendKpis = ({ source, rows, meta }) => {
  const latest = meta?.latest || {};
  const baselineCurrent = meta?.baseline?.totalCurrentRms;
  const measuredEnergy = meta?.measuredEnergyDeltaKwh;
  const estimatedEnergy = meta?.estimatedActiveEnergyKwh;
  const hasEnergy = hasNumber(measuredEnergy) || hasNumber(estimatedEnergy);
  const peakActivePower = getOverviewPeakValue(rows, "totalActivePower");
  const peakApparentPower = getOverviewPeakValue(
    rows,
    "totalApparentPower",
    meta?.peakApparentPower,
  );
  const reportingText = getOverviewTrendReportingText(source, meta);

  const kpis = [
    {
      label: "Current now",
      value: formatOverviewTrendMetric(latest.totalCurrentRms, "A", 2),
      meta: hasNumber(latest.totalCurrentRms)
        ? `Latest ${meta?.bucketMinutes || POWER_TREND_BUCKET_MINUTES} min avg`
        : "No current reading",
    },
    {
      label: "Change vs baseline",
      value: formatOverviewTrendPercent(meta?.currentChangePct),
      tone: getOverviewTrendChangeTone(meta),
      meta: hasNumber(baselineCurrent)
        ? `Baseline ${formatOverviewNumber(baselineCurrent, 2)} A`
        : "Vs earlier buckets",
    },
  ];

  if (hasEnergy) {
    const energyValue = hasNumber(measuredEnergy)
      ? measuredEnergy
      : estimatedEnergy;
    kpis.push({
      label: "Energy used",
      value: formatOverviewTrendMetric(energyValue, "kWh", 2),
      meta: hasNumber(measuredEnergy)
        ? "Measured kWh counter"
        : "Estimated from active power",
    });
  } else {
    kpis.push({
      label: "Observed range",
      value: formatOverviewCurrentRange(rows),
      meta: "Low/high over 24h",
    });
  }

  if (hasNumber(peakActivePower)) {
    kpis.push({
      label: "Peak active power",
      value: formatOverviewTrendMetric(peakActivePower, "W", 0),
      meta: "Highest bucket total",
    });
  } else if (hasNumber(peakApparentPower)) {
    kpis.push({
      label: "Peak apparent power",
      value: formatOverviewTrendMetric(peakApparentPower, "VA", 0),
      meta: "Highest bucket total",
    });
  } else {
    kpis.push({
      label: "Reporting",
      value: reportingText.split(" reporting ")[0] || "-",
      meta: reportingText.includes(" reporting ")
        ? `${getTrendSourceLabel(source)} reporting current`
        : reportingText,
    });
  }

  return kpis;
};

const toTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getTime();
};

const getEventTypeName = (code) =>
  EVENT_NAME_MAP.get(String(code)) || `Event Type ${code}`;

const getActivityTypeActions = (typeKey) => {
  if (typeKey === "all") {
    return null;
  }

  return ACTIVITY_ACTIONS_BY_FILTER[typeKey] || [];
};

const sourceAllowsActivityLogs = (sourceKey) => sourceKey !== "alerts";

const sourceAllowsEventLogs = (sourceKey, typeKey) =>
  typeKey === "all" && sourceKey !== "activity";

const getEventGroupKey = (row) =>
  [
    row?.eventId || "",
    row?.eventType || "",
    row?.eventName || "",
  ].join("|");

const compactRepeatedEventRows = (rows) => {
  const compacted = [];
  let eventGroup = [];
  let eventGroupKey = "";

  const flushEventGroup = () => {
    if (!eventGroup.length) {
      return;
    }

    if (eventGroup.length < 3) {
      compacted.push(...eventGroup);
    } else {
      const latest = eventGroup[0];
      compacted.push({
        ...latest,
        id: `event-group-${getEventGroupKey(latest)}-${latest.id}`,
        source: "eventGroup",
        eventCount: eventGroup.length,
        triggeredCount: eventGroup.filter((event) => event?.triggered).length,
        clearedCount: eventGroup.filter((event) => !event?.triggered).length,
      });
    }

    eventGroup = [];
    eventGroupKey = "";
  };

  (rows || []).forEach((row) => {
    if (row?.source !== "event") {
      flushEventGroup();
      compacted.push(row);
      return;
    }

    const nextKey = getEventGroupKey(row);
    if (eventGroup.length && eventGroupKey !== nextKey) {
      flushEventGroup();
    }

    eventGroup.push(row);
    eventGroupKey = nextKey;
  });

  flushEventGroup();
  return compacted;
};

const getActivityPresentation = (row) =>
  ACTIVITY_PRESENTATION_BY_ACTION[row?.action] || {
    category: "Activity",
    icon: ACTIVITY_ICON_CONFIG[row?.action]?.icon || "history",
    tone: "neutral",
  };

const getActorLabel = (row) =>
  row?.actorEmail || row?.actorName || (row?.actorUserId ? `User ${row.actorUserId}` : "");

const getActivityStatusMeta = (row) => {
  const actionText = String(row?.action || "").toLowerCase();
  const rowText = [row?.action, row?.description, row?.details]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/(retry|fail|failed|failure|error|denied|rejected)/.test(rowText)) {
    return { label: "Attention", tone: "danger" };
  }
  if (actionText.includes("queued")) {
    return { label: "Queued", tone: "warning" };
  }
  if (actionText.includes("completed") || actionText.includes("updated")) {
    return { label: "Complete", tone: "success" };
  }
  return { label: "Logged", tone: "neutral" };
};

const normalizeSerial = (value) =>
  value === undefined || value === null ? null : String(value);

const getDisplayName = (device) =>
  device?.queuedName || device?.deviceName || "";

const OverviewKvItem = ({
  label,
  value,
  note,
  actionIcon,
  actionAriaLabel,
  to,
  onClick,
  fullWidth = false,
}) => {
  const interactive = Boolean(to || onClick);
  const className = [
    "device-overview__kv-item",
    fullWidth ? "device-overview__kv-item--full" : "",
    interactive ? "device-overview__kv-item--interactive" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <div className="device-overview__kv-label">{label}</div>
      <div className="device-overview__kv-value">{value}</div>
      {note ? <div className="device-overview__kv-note">{note}</div> : null}
      {interactive && actionIcon ? (
        <span className="device-overview__kv-icon" aria-hidden="true">
          <i className="material-icons" style={{ marginRight: "0px", marginLeft: "3px"}}>{actionIcon}</i>
        </span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link
        className={`${className} device-overview__kv-item-link`}
        to={to}
        aria-label={actionAriaLabel}
        title={actionAriaLabel}
      >
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={`${className} device-overview__kv-item-button`}
        onClick={onClick}
        aria-label={actionAriaLabel}
        title={actionAriaLabel}
      >
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
};

const RenameDeviceNameModalBody = ({
  currentName,
  pendingName,
  nameSyncStatus,
  deviceOffline,
  onSubmit,
  close,
  dismiss,
}) => {
  const [nextName, setNextName] = useState(currentName || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const trimmedName = nextName.trim();
  const canSubmit =
    Boolean(trimmedName) &&
    (trimmedName !== String(currentName || "").trim() || nameSyncStatus === "failed") &&
    !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!trimmedName) {
      setError("Device name is required.");
      return;
    }

    if (!canSubmit) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      await onSubmit(trimmedName);
      close();
    } catch (err) {
      console.error(err);
      setError(apiErrorMessageFromError(err, "Unable to update device name."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="device-overview__modal-form" onSubmit={handleSubmit}>
      <div className="device-overview__modal-copy">
        Rename this device from the overview page without leaving the current
        workflow.
      </div>

      <div className="device-overview__modal-field">
        <label
          className="device-overview__modal-label"
          htmlFor="overview-device-name-input"
        >
          Device name
        </label>
        <input
          id="overview-device-name-input"
          className="device-overview__input"
          value={nextName}
          onChange={(event) => {
            setNextName(event.target.value);
            setError("");
          }}
          placeholder="Enter device name"
          autoFocus
          disabled={submitting}
        />
      </div>

      {deviceOffline ? (
        <div className="device-overview__modal-note device-overview__modal-note--warning">
          Device is offline. Name changes are queued and will apply once it
          reconnects.
        </div>
      ) : null}

      {pendingName ? (
        <div className="device-overview__modal-note">
          {nameSyncStatus === "failed" ? "Name sync failed: " : "Pending rename request: "}
          <strong>{pendingName}</strong>
        </div>
      ) : null}

      {error ? (
        <div className="device-overview__inline-error">{error}</div>
      ) : null}

      <div className="device-overview__modal-actions">
        <button
          type="button"
          className="button"
          style={{
            backgroundColor: "var(--app-control-disabled-bg)",
            color: "var(--app-control-disabled-text)",
            marginBottom: 0,
          }}
          onClick={dismiss}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="button"
          style={{ marginBottom: 0 }}
          disabled={!canSubmit}
        >
          {submitting ? "Saving..." : "Save Name"}
        </button>
      </div>
    </form>
  );
};

const DeviceOverviewPowerTrendSnapshot = ({
  serial,
  canViewInlets,
  canViewOutlets,
  deviceOffline,
}) => {
  const [trendState, setTrendState] = useState({
    loading: false,
    loaded: false,
    source: canViewInlets ? "inlet" : "outlet",
    rows: [],
    meta: {},
    error: "",
  });

  useEffect(() => {
    if (!serial || (!canViewInlets && !canViewOutlets)) {
      setTrendState({
        loading: false,
        loaded: true,
        source: canViewInlets ? "inlet" : "outlet",
        rows: [],
        meta: {},
        error: "",
      });
      return undefined;
    }

    const cancelSource = axios.CancelToken.source();
    let mounted = true;

    const fetchTrendSource = async (source) => {
      const params = buildOverviewTrendParams(serial);
      const response = await axios.get(`/v2/${source}s/trends?${params}`, {
        headers: getAuthHeaders(),
        cancelToken: cancelSource.token,
      });
      return normalizeTrendPayload(response?.data, source);
    };

    const loadTrends = async () => {
      setTrendState((prev) => ({
        ...prev,
        loading: true,
        loaded: false,
        error: "",
      }));

      try {
        let payload = null;
        let firstError = null;

        if (canViewInlets) {
          try {
            payload = await fetchTrendSource("inlet");
          } catch (error) {
            if (axios.isCancel(error)) return;
            firstError = error;
          }
        }

        if (canViewOutlets && (!payload || payload.rows.length === 0)) {
          try {
            const outletPayload = await fetchTrendSource("outlet");
            if (!payload || outletPayload.rows.length > 0) {
              payload = outletPayload;
            }
          } catch (error) {
            if (axios.isCancel(error)) return;
            if (!payload) {
              throw error;
            }
          }
        }

        if (!payload && firstError) {
          throw firstError;
        }

        if (!mounted) return;
        setTrendState({
          loading: false,
          loaded: true,
          source: payload?.source || (canViewInlets ? "inlet" : "outlet"),
          rows: payload?.rows || [],
          meta: payload?.meta || {},
          error: "",
        });
      } catch (error) {
        if (axios.isCancel(error) || !mounted) return;
        setTrendState({
          loading: false,
          loaded: true,
          source: canViewInlets ? "inlet" : "outlet",
          rows: [],
          meta: {},
          error:
            error?.response?.data?.message ||
            "Unable to load power trend data.",
        });
      }
    };

    loadTrends();

    return () => {
      mounted = false;
      cancelSource.cancel("Canceling overview power trend request");
    };
  }, [serial, canViewInlets, canViewOutlets]);

  if (!canViewInlets && !canViewOutlets) {
    return null;
  }

  const source = trendState.source || (canViewInlets ? "inlet" : "outlet");
  const rows = trendState.rows || [];
  const meta = trendState.meta || {};
  const status = getOverviewTrendStatus({
    rows,
    meta,
    error: trendState.error,
  });
  const kpis = buildOverviewTrendKpis({ source, rows, meta });
  const latestTime = formatOverviewTrendTime(meta?.latest?.createdAt);
  const reportingText = getOverviewTrendReportingText(source, meta);
  const detailPath = getTrendDetailPath(serial, source);
  const footerParts = [
    `${meta?.bucketMinutes || POWER_TREND_BUCKET_MINUTES} min buckets`,
    reportingText,
    latestTime
      ? `${deviceOffline ? "historical through" : "updated"} ${latestTime}`
      : null,
  ].filter(Boolean);

  return (
    <section className="device-overview__card device-overview__trend-card">
      <div className="device-overview__card-header">
        <div>
          <h3 className="device-overview__card-title">Power Trend Snapshot</h3>
          <div className="device-overview__card-kicker">
            Current RMS, last 24 hours
          </div>
        </div>
        <div className="device-overview__row-actions">
          <Link className="device-overview__sub-link" to={detailPath}>
            View trends
          </Link>
        </div>
      </div>

      {trendState.loading && !trendState.loaded ? (
        <div className="device-overview__stack">
          <SkeletonLine width="48%" height={18} />
          <SkeletonLine width="82%" />
          <div className="device-overview__trend-kpi-grid">
            {[0, 1, 2, 3].map((index) => (
              <div
                key={`power-trend-skeleton-${index}`}
                className="device-overview__trend-kpi"
              >
                <SkeletonLine width="66%" />
                <SkeletonLine width="46%" height={20} />
                <SkeletonLine width="78%" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div
            className={`device-overview__trend-status device-overview__trend-status--${status.tone}`}
          >
            <i className="material-icons device-overview__trend-status-icon">
              {status.tone === "danger"
                ? "priority_high"
                : status.tone === "warning"
                  ? "warning"
                  : status.tone === "success"
                    ? "show_chart"
                    : "info"}
            </i>
            <div className="device-overview__trend-status-copy">
              <div className="device-overview__trend-status-title">
                {status.label}
              </div>
              <div className="device-overview__trend-status-detail">
                {status.detail}
              </div>
            </div>
          </div>

          {!trendState.error && rows.length > 0 ? (
            <>
              <div className="device-overview__trend-kpi-grid">
                {kpis.map((kpi) => (
                  <div
                    key={kpi.label}
                    className={`device-overview__trend-kpi ${
                      kpi.tone ? `device-overview__trend-kpi--${kpi.tone}` : ""
                    }`}
                  >
                    <div className="device-overview__trend-kpi-label">
                      {kpi.label}
                    </div>
                    <div className="device-overview__trend-kpi-value">
                      {kpi.value}
                    </div>
                    {kpi.meta ? (
                      <div className="device-overview__trend-kpi-meta">
                        {kpi.meta}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="device-overview__trend-footer">
                {footerParts.map((part, index) => (
                  <React.Fragment key={part}>
                    {index > 0 ? (
                      <span className="device-overview__trend-footer-separator">
                        ·
                      </span>
                    ) : null}
                    <span>{part}</span>
                  </React.Fragment>
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </section>
  );
};

const extractGroupItemId = (item) => {
  if (item === undefined || item === null) return null;
  if (typeof item === "string" || typeof item === "number") {
    return String(item);
  }

  return normalizeSerial(
    item.item_id ?? item.id ?? item.enclosureSerialNumber ?? item.serial
  );
};

const groupContainsDevice = (group, enclosureSerialNumber) => {
  const serial = normalizeSerial(enclosureSerialNumber);
  if (!serial) return false;

  const items = Array.isArray(group?.items) ? group.items : [];
  return items.some((item) => extractGroupItemId(item) === serial);
};

const getOutletOperationConfigs = (outlet) => {
  const configs = [];

  if (outlet?.state === "OFF") {
    configs.push({
      key: "turn-on",
      label: "Turn On",
      icon: "offline_bolt",
      nextState: "ON",
      actionLabel: "Turn On",
      title: "Switch Outlet from OFF state to ON state",
      tone: "on",
    });
  }

  if (outlet?.state === "ON") {
    configs.push({
      key: "turn-off",
      label: "Turn Off",
      icon: "pause_circle",
      nextState: "OFF",
      actionLabel: "Turn Off",
      title: "Switch Outlet from ON state to OFF state",
      tone: "off",
    });
  }

  if (outlet?.state) {
    configs.push({
      key: "cycle",
      label: "Cycle",
      icon: "replay",
      nextState: "REBOOT",
      actionLabel: "Cycle",
      title: "Reboot outlet with configured reboot time",
      tone: "cycle",
    });
  }

  return configs;
};

const getAlertActiveSince = (event) => {
  const epochSeconds = Number(event?.timeExecutedInEpoch);
  if (Number.isFinite(epochSeconds) && epochSeconds > 0) {
    return epochSeconds * 1000;
  }

  return (
    toTimestamp(event?.lastChange) ||
    toTimestamp(event?.triggeredAt) ||
    toTimestamp(event?.createdAt) ||
    toTimestamp(event?.updatedAt)
  );
};

const getAlertAttention = (durationMs) => {
  if (!Number.isFinite(durationMs)) {
    return null;
  }

  if (durationMs >= ALERT_STALE_THRESHOLD_MS) {
    return { label: "Stale", tone: "danger" };
  }

  if (durationMs >= ALERT_PERSISTENT_THRESHOLD_MS) {
    return { label: "Persistent", tone: "warning" };
  }

  if (durationMs <= ALERT_NEW_THRESHOLD_MS) {
    return { label: "New", tone: "neutral" };
  }

  return null;
};

const formatAlertStartedAt = (timestamp) => {
  if (!Number.isFinite(timestamp)) {
    return "";
  }

  try {
    return ALERT_STARTED_FORMATTER.format(new Date(timestamp));
  } catch (error) {
    return "";
  }
};

const DeviceOverview = ({
  match,
  device,
  conf,
  confLoading,
  events,
  eventsLoading,
  outlets,
  outletsLoading,
  updateOutlet,
  resources,
  user,
  deviceOffline,
  offlineReason,
  shellState,
  effectivePermissions,
  modifyResource,
  refreshDevice,
  onDeviceDataUpdate,
}) => {
  const serial = String(
    device?.enclosureSerialNumber ||
      match?.params?.enclosureSerialNumber ||
      ""
  );

  const permissionsReady = Boolean(effectivePermissions?.ready);
  const canViewEvents = permissionsReady
    ? effectivePermissions?.canViewEvents
    : false;
  const canViewInlets = permissionsReady
    ? effectivePermissions?.canViewInlets
    : false;
  const canViewOutlets = permissionsReady
    ? effectivePermissions?.canViewOutlets
    : false;
  const canViewActivityLog = permissionsReady
    ? effectivePermissions?.canViewActivityLog
    : false;
  const canViewFirmware = permissionsReady
    ? effectivePermissions?.canViewFirmware
    : false;
  const canEditDeviceInfo = permissionsReady
    ? effectivePermissions?.canEditDeviceInfo
    : false;
  const canSwitchOutlets = permissionsReady
    ? effectivePermissions?.canSwitchOutlets
    : false;

  const [networkStatus, setNetworkStatus] = useState(null);
  const [networkStatusLoading, setNetworkStatusLoading] = useState(false);
  const [networkStatusError, setNetworkStatusError] = useState("");
  const [networkStatusUnavailable, setNetworkStatusUnavailable] =
    useState(false);

  const [pingHost, setPingHost] = useState("");
  const [pingLoading, setPingLoading] = useState(false);
  const [pingStatus, setPingStatus] = useState("");
  const [pingMessage, setPingMessage] = useState("");

  const [outletLoadingMap, setOutletLoadingMap] = useState({});

  const [logsRows, setLogsRows] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsMeta, setLogsMeta] = useState({
    currentPage: 1,
    limit: DEFAULT_LOG_LIMIT,
    totalCount: 0,
    totalPages: 0,
    availableEventTypes: [],
    permissions: {
      canViewActivity: true,
      canViewEvents: true,
    },
  });
  const [logsPage, setLogsPage] = useState(1);
  const [logsRefreshKey, setLogsRefreshKey] = useState(0);

  const [activitySourceKey, setActivitySourceKey] = useState("all");
  const [activityTypeKey, setActivityTypeKey] = useState("all");

  const initialTimeRange = useMemo(
    () => getDefaultTimeRange(DEFAULT_TIMEFRAME_KEY),
    []
  );
  const [timeframeKey, setTimeframeKey] = useState(DEFAULT_TIMEFRAME_KEY);
  const [appliedRange, setAppliedRange] = useState(initialTimeRange);
  const [customRange, setCustomRange] = useState([
    initialTimeRange?.since ? new Date(initialTimeRange.since) : null,
    initialTimeRange?.before ? new Date(initialTimeRange.before) : null,
  ]);
  const [customError, setCustomError] = useState("");

  const deviceGroups = useMemo(() => {
    const allGroups = Array.isArray(resources?.groups) ? resources.groups : [];
    return allGroups.filter((group) => {
      if (group?.groupType !== "devices") return false;
      return groupContainsDevice(group, serial);
    });
  }, [resources?.groups, serial]);

  const sortedOutlets = useMemo(() => {
    const list = Array.isArray(outlets) ? outlets : [];
    return [...list]
      .filter((outlet) => String(outlet?.enclosureSerialNumber) === serial)
      .sort((left, right) => {
        const lNum = left?.pduLabeledOutletNum ?? left?.outlet_id ?? 0;
        const rNum = right?.pduLabeledOutletNum ?? right?.outlet_id ?? 0;
        return lNum - rNum;
      });
  }, [outlets, serial]);

  const topOutlets = useMemo(() => sortedOutlets.slice(0, 8), [sortedOutlets]);

  const activeAlerts = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    const now = Date.now();

    return list
      .filter(
        (event) =>
          Boolean(event?.triggered) &&
          Number(event?.code) !== TIME_INTERVAL_BETWEEN_TRIGGERS_EVENT_CODE
      )
      .map((event) => {
        const activeSince = getAlertActiveSince(event);
        const durationMs = Number.isFinite(activeSince)
          ? Math.max(0, now - activeSince)
          : null;

        return {
          event,
          activeSince,
          durationMs,
          attention: getAlertAttention(durationMs),
        };
      })
      .sort((left, right) => {
        const leftDuration = Number.isFinite(left?.durationMs)
          ? left.durationMs
          : -1;
        const rightDuration = Number.isFinite(right?.durationMs)
          ? right.durationMs
          : -1;
        if (leftDuration !== rightDuration) {
          return rightDuration - leftDuration;
        }

        const leftTs = toTimestamp(left?.event?.lastChange) || 0;
        const rightTs = toTimestamp(right?.event?.lastChange) || 0;
        return rightTs - leftTs;
      });
  }, [events]);

  const activeAlertCount = activeAlerts.length;
  const visibleActiveAlerts = useMemo(
    () => activeAlerts.slice(0, MAX_OVERVIEW_ACTIVE_ALERTS),
    [activeAlerts]
  );
  const hiddenActiveAlertCount = Math.max(
    0,
    activeAlertCount - visibleActiveAlerts.length
  );
  const alertsCtaLabel = eventsLoading
    ? "View alerts"
    : activeAlertCount > 0
      ? `Review active alerts (${activeAlertCount})`
      : "View alert history";

  const totalAlerts = Array.isArray(events)
    ? events.filter(
        (event) =>
          Number(event?.code) !== TIME_INTERVAL_BETWEEN_TRIGGERS_EVENT_CODE
      ).length
    : 0;
  const healthyAlerts = Array.isArray(events)
    ? events.filter(
        (event) =>
          !event?.triggered &&
          Number(event?.code) !== TIME_INTERVAL_BETWEEN_TRIGGERS_EVENT_CODE
      ).length
    : 0;

  const networkInterfaceRows = useMemo(
    () => buildInterfaceRows(networkStatus, conf),
    [networkStatus, conf]
  );

  const activeUplinkSummary = useMemo(
    () => getActiveUplinkSummaryFromConf(conf || {}, networkStatus),
    [conf, networkStatus]
  );

  const primaryInterfaceSummary = useMemo(
    () => getWanPrioritySummary(conf || {}, networkStatus),
    [conf, networkStatus]
  );
  const apiVersionValue = (() => {
    if (typeof device?.apiVersion === "string") {
      return device.apiVersion.trim();
    }
    if (device?.apiVersion === undefined || device?.apiVersion === null) {
      return "";
    }
    return String(device.apiVersion).trim();
  })();
  const controllerFirmwareValue = (() => {
    if (typeof device?.controllerFirmwareVersion === "string") {
      return device.controllerFirmwareVersion.trim();
    }
    if (
      device?.controllerFirmwareVersion === undefined ||
      device?.controllerFirmwareVersion === null
    ) {
      return "";
    }
    return String(device.controllerFirmwareVersion).trim();
  })();
  const controllerApiCombo = (() => {
    const controllerPart = controllerFirmwareValue || "-";
    const apiPart = apiVersionValue || "1.0";

    if (controllerPart === "-" && apiPart === "-") {
      return "-";
    }

    return `${controllerPart} / ${apiPart}`;
  })();
  const connectionLabel =
    device?.connectionState === "Connected" ? "Online" : "Offline";
  const displayName = getDisplayName(device);
  const pendingName = device?.queuedName || "";
  const configurationBasePath = `/devices/connected/${serial}/configuration`;
  const deviceConfigurationPath = `${configurationBasePath}/device`;
  const networkConfigurationPath = `${configurationBasePath}/network`;
  const firmwareConfigurationPath = `${configurationBasePath}/firmware`;

  const shellLinkAvailable = Boolean(
    shellState?.showShellTab && shellState?.isCompatible
  );
  const shellStatusText = shellLinkAvailable
    ? ""
    : !shellState?.showShellTab
      ? "Admin only"
      : shellState?.warningMessage || "Unavailable for this firmware/API version";

  const fetchNetworkStatus = useCallback(async () => {
    if (!serial) return;

    const cached = readNetworkStatusCache(serial);
    if (cached?.fresh && (cached.data || cached.unavailable)) {
      setNetworkStatus(cached.data || null);
      setNetworkStatusUnavailable(Boolean(cached.unavailable));
      setNetworkStatusError("");
      return;
    }

    if (cached && (cached.data || cached.unavailable)) {
      setNetworkStatus(cached.data || null);
      setNetworkStatusUnavailable(Boolean(cached.unavailable));
      setNetworkStatusError("");
    }

    setNetworkStatusLoading(true);
    setNetworkStatusError("");

    try {
      const response = await fetchNetworkStatusWithCache({
        serial,
        headers: getAuthHeaders(),
      });
      if (response?.unavailable) {
        setNetworkStatusUnavailable(true);
        setNetworkStatus(null);
        return;
      }
      setNetworkStatus(response?.data || {});
      setNetworkStatusUnavailable(false);
    } catch (error) {
      if (isUnknownApiEndpointError(error)) {
        setNetworkStatusUnavailable(true);
        setNetworkStatusError("");
        setNetworkStatus(null);
        writeNetworkStatusCache(serial, {
          data: null,
          unavailable: true,
        });
      } else {
        const message =
          error?.response?.data?.message ||
          "Unable to retrieve network connectivity status from this device.";
        setNetworkStatusError(message);
      }
    } finally {
      setNetworkStatusLoading(false);
    }
  }, [serial]);

  useEffect(() => {
    if (!serial) return;
    fetchNetworkStatus();
  }, [serial, fetchNetworkStatus]);

  useEffect(() => {
    if (!serial) return;
    setLogsPage(1);
    setActivitySourceKey("all");
    setActivityTypeKey("all");
  }, [serial]);

  const fetchLogs = useCallback(async () => {
    if (!serial || !permissionsReady || (!canViewActivityLog && !canViewEvents)) {
      return;
    }

    setLogsLoading(true);

    try {
      const encodedSerial = encodeURIComponent(serial);
      const params = {
        page: logsPage,
        limit: DEFAULT_LOG_LIMIT,
      };

      const allowActivityLogs = sourceAllowsActivityLogs(activitySourceKey);
      const allowEventLogs = sourceAllowsEventLogs(
        activitySourceKey,
        activityTypeKey,
      );
      const activityActions = getActivityTypeActions(activityTypeKey);

      if (!allowActivityLogs) {
        params.activityActions = "__none__";
      } else if (Array.isArray(activityActions)) {
        params.activityActions = activityActions.length
          ? activityActions.join(",")
          : "__none__";
      }

      if (!allowEventLogs) {
        params.eventTypes = "__none__";
      }

      if (activitySourceKey === "attention") {
        params.attentionOnly = "true";
      }

      if (Number.isFinite(appliedRange?.since)) {
        params.since = Math.floor(appliedRange.since);
      }
      if (Number.isFinite(appliedRange?.before)) {
        params.before = Math.floor(appliedRange.before);
      }

      const response = await axios.get(`/v2/devices/${encodedSerial}/logs`, {
        params,
        headers: getAuthHeaders(),
      });

      const payloadRows = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const payloadMeta = response?.data?.meta || {};

      setLogsRows(payloadRows);
      setLogsMeta({
        currentPage: payloadMeta?.currentPage || logsPage,
        limit: payloadMeta?.limit || DEFAULT_LOG_LIMIT,
        totalCount: payloadMeta?.totalCount || 0,
        totalPages: payloadMeta?.totalPages || 0,
        availableEventTypes: Array.isArray(payloadMeta?.availableEventTypes)
          ? payloadMeta.availableEventTypes
          : [],
        permissions: {
          canViewActivity: payloadMeta?.permissions?.canViewActivity !== false,
          canViewEvents: payloadMeta?.permissions?.canViewEvents !== false,
        },
      });
    } catch (error) {
      console.error("Failed to fetch device logs", error);
      const status = error?.response?.status;
      if (status === 403) {
        setLogsRows([]);
        setLogsMeta((prev) => ({
          ...prev,
          totalCount: 0,
          totalPages: 0,
          permissions: {
            canViewActivity: false,
            canViewEvents: false,
          },
        }));
      }
    } finally {
      setLogsLoading(false);
    }
  }, [
    serial,
    permissionsReady,
    canViewActivityLog,
    canViewEvents,
    logsPage,
    activitySourceKey,
    activityTypeKey,
    appliedRange?.since,
    appliedRange?.before,
  ]);

  const displayedLogsRows = useMemo(
    () => compactRepeatedEventRows(logsRows),
    [logsRows],
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, logsRefreshKey]);

  const handleDeviceRename = useCallback(
    (nextName) =>
      new Promise((resolve, reject) => {
        if (!modifyResource || !serial) {
          reject(new Error("Unable to update device name."));
          return;
        }

        modifyResource(
          "devices",
          serial,
          { deviceName: nextName },
          (err, response) => {
            if (err) {
              const failedName = err.response?.data?.data;
              if (failedName?.nameSyncStatus) onDeviceDataUpdate?.(failedName);
              reject(err);
              return;
            }

            const payload = response?.data || { deviceName: nextName };
            onDeviceDataUpdate?.({
              ...payload,
              deviceName: payload?.deviceName || nextName,
              queuedName: Object.prototype.hasOwnProperty.call(payload, "queuedName")
                ? payload.queuedName
                : payload?.queued_name ?? device?.queuedName,
            });
            refreshDevice?.();
            resolve(payload);
          },
          "enclosureSerialNumber"
        );
      }),
    [device?.queuedName, modifyResource, onDeviceDataUpdate, refreshDevice, serial]
  );

  const openRenameDeviceModal = useCallback(() => {
    if (!canEditDeviceInfo) {
      return;
    }

    ModalManager.confirm(
      "",
      "Edit Device Name",
      "",
      (submitted, closeModal) => {
        if (submitted && typeof closeModal === "function") {
          closeModal();
        }
      },
      ({ close, dismiss }) => (
        <RenameDeviceNameModalBody
          currentName={displayName}
          pendingName={pendingName}
          nameSyncStatus={device?.nameSyncStatus}
          deviceOffline={deviceOffline}
          onSubmit={handleDeviceRename}
          close={close}
          dismiss={dismiss}
        />
      ),
      true,
      true,
      RENAME_MODAL_OPTIONS
    );
  }, [
    canEditDeviceInfo,
    deviceOffline,
    device?.nameSyncStatus,
    displayName,
    handleDeviceRename,
    pendingName,
  ]);

  const handlePingSubmit = async () => {
    if (!serial || pingLoading) return;
    if (!pingHost.trim()) {
      setPingStatus("error");
      setPingMessage("Host is required.");
      return;
    }

    setPingLoading(true);
    setPingStatus("");
    setPingMessage("");

    try {
      const encodedSerial = encodeURIComponent(serial);
      const response = await axios.post(
        `/v1/devices/${encodedSerial}/diagnostics/ping`,
        { host: pingHost.trim() },
        { headers: getAuthHeaders() }
      );

      const successMessage =
        response?.data?.data?.message ||
        response?.data?.data?.result ||
        "Ping successful.";
      setPingStatus("success");
      setPingMessage(successMessage);
      NotificationManager.success("Ping succeeded");
    } catch (error) {
      const text =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Ping failed. Please try again.";
      setPingStatus("error");
      setPingMessage(text);
      NotificationManager.alert(text);
    } finally {
      setPingLoading(false);
    }
  };

  const runOutletAction = (outlet, nextState, actionLabel) => {
    if (!outlet || !updateOutlet) return;

    const outletId = outlet.outlet_id;
    const confirmActionLabel =
      nextState === "REBOOT" ? "power cycle" : actionLabel.toLowerCase();
    ModalManager.confirm(
      `Are you sure you want to ${confirmActionLabel} Outlet ${
        outlet.pduLabeledOutletNum || outletId
      }?`,
      "Are you sure?",
      actionLabel,
      (submitted, close) => {
        if (!submitted) {
          return;
        }

        if (typeof close === "function") {
          close();
        }

        setOutletLoadingMap((prev) => ({
          ...prev,
          [outletId]: true,
        }));

        updateOutlet(outletId, { state: nextState }, () => {
          setOutletLoadingMap((prev) => ({
            ...prev,
            [outletId]: false,
          }));
        });
      }
    );
  };

  const handleTimeframeChange = (event) => {
    const nextKey = event?.target?.value || DEFAULT_TIMEFRAME_KEY;
    setCustomError("");

    if (nextKey === "custom") {
      setTimeframeKey(nextKey);
      return;
    }

    const preset = findTimeframePreset(nextKey);
    const nextRange = preset?.durationMs
      ? createRangeFromDuration(preset.durationMs)
      : getDefaultTimeRange(DEFAULT_TIMEFRAME_KEY);

    setLogsPage(1);
    setTimeframeKey(nextKey);
    setAppliedRange(nextRange);
    setCustomRange([
      nextRange?.since ? new Date(nextRange.since) : null,
      nextRange?.before ? new Date(nextRange.before) : null,
    ]);
  };

  const applyCustomRange = () => {
    const [startDate, endDate] = customRange || [];
    if (!startDate || !endDate) {
      setCustomError("Select both start and end times.");
      return;
    }

    if (startDate.getTime() >= endDate.getTime()) {
      setCustomError("Start time must be before end time.");
      return;
    }

    setLogsPage(1);
    setCustomError("");
    setAppliedRange({
      since: startDate.getTime(),
      before: endDate.getTime(),
    });
    setTimeframeKey("custom");
  };

  const renderActivityLink = (log) => {
    const linkTemplate = ACTIVITY_ICON_CONFIG[log?.action]?.url;
    if (!linkTemplate) return null;

    const resolved = linkTemplate
      .replace(":resourceId", log?.resourceId || "")
      .replace(":enclosureSn", log?.enclosureSn || serial || "");

    if (!resolved || resolved.includes(":")) {
      return null;
    }

    return resolved;
  };

  const renderLogRow = (row) => {
    const timestamp = Number.isFinite(row?.timestamp)
      ? new Date(row.timestamp)
      : row?.createdAt
        ? new Date(row.createdAt)
        : row?.deviceTs
          ? new Date(row.deviceTs)
          : row?.receivedAt
            ? new Date(row.receivedAt)
            : null;

    const relativeTime =
      timestamp && !Number.isNaN(timestamp.getTime())
        ? formatDistanceToNow(timestamp, { addSuffix: true })
        : "Unknown time";

    const renderDetailParts = (parts) => {
      const visibleParts = parts.filter(Boolean);
      if (!visibleParts.length) {
        return null;
      }

      return (
        <div className="device-overview__activity-details">
          {visibleParts.map((part, index) => (
            <React.Fragment key={`activity-detail-${row.id}-${index}`}>
              {index > 0 ? (
                <span className="device-overview__activity-separator">·</span>
              ) : null}
              <span>{part}</span>
            </React.Fragment>
          ))}
        </div>
      );
    };

    if (row?.source === "event" || row?.source === "eventGroup") {
      const eventName = row?.eventName || getEventTypeName(row?.eventType);
      const eventTypeLabel = getEventTypeName(row?.eventType);
      const eventLink = row?.eventId
        ? buildAlertDetailsPath(serial, row.eventId)
        : null;
      const eventStatus = row?.triggered
        ? { label: "Triggered", tone: "danger" }
        : { label: "Cleared", tone: "success" };
      const isEventGroup = row?.source === "eventGroup";
      const eventActionLabel = isEventGroup
        ? `Alert toggled ${row.eventCount} times`
        : `Alert ${eventStatus.label.toLowerCase()}`;
      const detailParts = isEventGroup
        ? [
            `${row.triggeredCount || 0} triggered`,
            `${row.clearedCount || 0} cleared`,
            eventTypeLabel,
            row?.eventId ? `Alert ${row.eventId}` : null,
          ]
        : [
            eventTypeLabel,
            row?.eventId ? `Alert ${row.eventId}` : null,
            "Device event",
          ];

      return (
        <div
          key={row.id}
          className={`device-overview__activity-row device-overview__activity-row--event device-overview__activity-row--${eventStatus.tone}`}
        >
          <div className="device-overview__activity-icon" aria-hidden="true">
            <i className="material-icons" style={{ width: "18px" }}>notifications</i>
          </div>
          <div className="device-overview__activity-main">
            <div className="device-overview__activity-title-line">
              <span
                className={`device-overview__activity-event-state device-overview__activity-event-state--${eventStatus.tone}`}
              >
                {eventActionLabel}
              </span>
              {eventLink ? (
                <Link className="device-overview__activity-title" to={eventLink}>
                  {eventName}
                </Link>
              ) : (
                <span className="device-overview__activity-title">{eventName}</span>
              )}
            </div>
            {renderDetailParts(detailParts)}
          </div>
          <div className="device-overview__activity-side device-overview__activity-side--time-only">
            <div className="device-overview__activity-time">{relativeTime}</div>
          </div>
        </div>
      );
    }

    const presentation = getActivityPresentation(row);
    const status = getActivityStatusMeta(row);
    const link = renderActivityLink(row);
    const actorLabel = getActorLabel(row);
    const resourceLabel = row?.resourceName || row?.resourceId || "";
    const title = row?.description || row?.action || "Activity";
    const details =
      row?.details && row.details !== title ? row.details : null;
    const resourcePart = link && resourceLabel ? (
      <Link className="device-overview__activity-detail-link" to={link}>
        {resourceLabel}
      </Link>
    ) : (
      resourceLabel || null
    );
    const detailParts = [
      resourcePart,
      details,
      actorLabel ? `by ${actorLabel}` : null,
      row?.counter > 1 ? `${row.counter} times in 5 minutes` : null,
    ];

    return (
      <div
        key={row.id}
        className={`device-overview__activity-row device-overview__activity-row--${presentation.tone}`}
      >
        <div className="device-overview__activity-icon" aria-hidden="true">
          <i className="material-icons">{presentation.icon}</i>
        </div>
        <div className="device-overview__activity-main">
          <div className="device-overview__activity-title-line">
            <span className="device-overview__activity-category">
              {presentation.category}
            </span>
            <span className="device-overview__activity-title">{title}</span>
          </div>
          {renderDetailParts(detailParts)}
        </div>
        <div className="device-overview__activity-side">
          <span
            className={`device-overview__activity-status device-overview__activity-status--${status.tone}`}
          >
            {status.label}
          </span>
          <div className="device-overview__activity-time">{relativeTime}</div>
        </div>
      </div>
    );
  };

  if (!device) {
    return (
      <div className="device-overview__page">
        <div className="device-overview__centered-loader">
          <Loading black />
        </div>
      </div>
    );
  }

  const activityPermission =
    permissionsReady &&
    canViewActivityLog &&
    logsMeta?.permissions?.canViewActivity !== false;
  const eventPermission =
    permissionsReady &&
    canViewEvents &&
    logsMeta?.permissions?.canViewEvents !== false;
  const visibleActivitySourceFilters = ACTIVITY_SOURCE_FILTERS.filter((filter) => {
    if (filter.key === "all" || filter.key === "attention") {
      return activityPermission || eventPermission;
    }
    if (filter.key === "alerts") {
      return eventPermission;
    }
    if (filter.key === "activity") {
      return activityPermission;
    }

    return activityPermission;
  });

  return (
    <div className="device-overview__page">
      <div className="device-overview__grid device-overview__grid--two-up">
        <section className="device-overview__card">
          <div className="device-overview__card-header">
            <h3 className="device-overview__card-title">Device Overview</h3>
            <span
              className={`device-overview__pill ${
                connectionLabel === "Online"
                  ? "device-overview__pill--success"
                  : "device-overview__pill--danger"
              }`}
            >
              {connectionLabel}
            </span>
          </div>
          <div className="device-overview__kv-grid">
            <OverviewKvItem
              label="Device Name"
              value={<>{displayName || "-"}<NameSyncStatus resource={device} /></>}
              note={
                device?.nameSyncStatus === "failed"
                  ? "The device rejected this name. Edit the name to try again."
                  : pendingName
                  ? "Name saved in CMP and awaiting confirmation from the device."
                  : deviceOffline
                  ? "Name changes are queued while the device is offline."
                    : ""
              }
              actionIcon={canEditDeviceInfo ? "edit" : undefined}
              actionAriaLabel={
                canEditDeviceInfo ? "Edit device name" : undefined
              }
              onClick={
                canEditDeviceInfo && modifyResource ? openRenameDeviceModal : undefined
              }
            />
            <OverviewKvItem label="Serial Number" value={serial || "-"} />
            <OverviewKvItem
              label="WAN Path"
              value={
                confLoading ? (
                  <SkeletonLine width="140px" />
                ) : (
                  renderNetworkSummaryValue(activeUplinkSummary)
                )
              }
              actionIcon="arrow_forward"
              actionAriaLabel="Open network settings"
              to={networkConfigurationPath}
            />
            <OverviewKvItem
              label="WAN Priority"
              value={
                confLoading ? (
                  <SkeletonLine width="120px" />
                ) : (
                  renderNetworkSummaryValue(primaryInterfaceSummary)
                )
              }
              actionIcon="arrow_forward"
              actionAriaLabel="Open network settings"
              to={networkConfigurationPath}
            />
            <OverviewKvItem
              label="Model Number"
              value={device?.modelNumber || "-"}
              actionIcon="arrow_forward"
              actionAriaLabel="Open device settings"
              to={deviceConfigurationPath}
            />
            <OverviewKvItem
              label="Controller Firmware / API Version"
              value={controllerApiCombo}
              actionIcon={canViewFirmware ? "arrow_forward" : undefined}
              actionAriaLabel={
                canViewFirmware ? "Open firmware settings" : undefined
              }
              to={canViewFirmware ? firmwareConfigurationPath : undefined}
            />
            <div className="device-overview__kv-item device-overview__kv-item--full">
              <div className="device-overview__kv-label">Device Groups</div>
              {resources?.groupsLoading && !resources?.groupsLoaded ? (
                <SkeletonLine width="220px" />
              ) : deviceGroups.length > 0 ? (
                <div className="device-overview__chip-wrap">
                  {deviceGroups.map((group) => (
                    <span
                      key={`${group?.id || group?.name}`}
                      className="device-overview__chip"
                    >
                      {group?.name || `Group ${group?.id}`}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="device-overview__kv-value">
                  Not in any device groups
                </div>
              )}
            </div>
          </div>
        </section>

        {permissionsReady && (canViewInlets || canViewOutlets) ? (
          <DeviceOverviewPowerTrendSnapshot
            serial={serial}
            canViewInlets={canViewInlets}
            canViewOutlets={canViewOutlets}
            deviceOffline={deviceOffline}
          />
        ) : null}
      </div>

      <div className="device-overview__grid device-overview__grid--two-up">
        {permissionsReady && canViewEvents ? (
        <section className="device-overview__card">
          <div className="device-overview__card-header">
            <h3 className="device-overview__card-title">Alerts Summary</h3>
            <Link
              className="device-overview__sub-link"
              to={buildDeviceAlertsPath(serial)}
            >
              {alertsCtaLabel}
            </Link>
          </div>

          <>
              {eventsLoading ? (
                <div className="device-overview__stack">
                  <SkeletonLine width="65%" />
                  <SkeletonLine width="42%" />
                  <SkeletonLine width="100%" />
                </div>
              ) : (
                <div
                  className={`device-overview__alert-status ${
                    activeAlertCount > 0
                      ? "device-overview__alert-status--danger"
                      : "device-overview__alert-status--success"
                  }`}
                >
                  <i className="material-icons device-overview__alert-status-icon">
                    {activeAlertCount > 0 ? "priority_high" : "check_circle"}
                  </i>
                  <div className="device-overview__alert-status-copy">
                    <div className="device-overview__alert-status-title">
                      {activeAlertCount > 0
                        ? `Action needed: ${activeAlertCount} active alert${
                            activeAlertCount === 1 ? "" : "s"
                          }`
                        : "All clear"}
                    </div>
                    <div className="device-overview__alert-health">
                      <span>{totalAlerts - healthyAlerts}</span> of{" "}
                      <span>{totalAlerts}</span> alerts triggered
                    </div>
                  </div>
                </div>
              )}

              <div className="device-overview__section-subtitle">
                Active alerts
                {!eventsLoading && activeAlertCount > 0
                  ? ` (${activeAlertCount})`
                  : ""}
              </div>
              {eventsLoading ? (
                <div className="device-overview__stack">
                  <SkeletonLine width="95%" />
                  <SkeletonLine width="80%" />
                </div>
              ) : activeAlertCount === 0 ? (
                <div className="device-overview__inline-muted">
                  No active alerts requiring attention.
                </div>
              ) : (
                <>
                  <div className="device-overview__list">
                    {visibleActiveAlerts.map(({ event, activeSince, attention }, index) => {
                      const eventName =
                        event?.name || getEventTypeName(event?.code);
                      const eventPath = event?.id
                        ? buildAlertDetailsPath(serial, event.id)
                        : buildDeviceAlertsPath(serial);
                      const activeForLabel = Number.isFinite(activeSince)
                        ? `${formatDistanceToNow(new Date(activeSince))} active`
                        : "Active";
                      const startedLabel = formatAlertStartedAt(activeSince);

                      return (
                        <div
                          key={`active-${event?.id || event?.code || "event"}-${index}`}
                          className="device-overview__list-row device-overview__list-row--alert"
                        >
                          <div className="device-overview__alert-row-main">
                            <div className="device-overview__alert-row-title">
                              <Link className="device-overview__log-link" to={eventPath}>
                                {eventName}
                              </Link>
                              {attention ? (
                                <span
                                  className={`device-overview__attention-chip device-overview__attention-chip--${attention.tone}`}
                                >
                                  {attention.label}
                                </span>
                              ) : null}
                            </div>
                            {startedLabel ? (
                              <div className="device-overview__list-meta device-overview__alert-started">
                                Started {startedLabel}
                              </div>
                            ) : null}
                          </div>
                          <div className="device-overview__alert-row-duration">
                            {activeForLabel}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {hiddenActiveAlertCount > 0 ? (
                    <div className="device-overview__alert-more">
                      <Link
                        className="device-overview__sub-link"
                        to={buildDeviceAlertsPath(serial)}
                      >
                        View {hiddenActiveAlertCount} more active alert
                        {hiddenActiveAlertCount === 1 ? "" : "s"}
                      </Link>
                    </div>
                  ) : null}
                </>
              )}
          </>
        </section>
        ) : null}

        <section className="device-overview__card">
          <div className="device-overview__card-header">
            <h3 className="device-overview__card-title">Network Connectivity</h3>
            <div className="device-overview__row-actions">
              <Link
                className="device-overview__sub-link"
                to={networkConfigurationPath}
              >
                Manage network
              </Link>
            </div>
          </div>

          {deviceOffline ? (
            <div className="device-overview__inline-muted">
              Device is offline. Live network status is unavailable until it reconnects.
            </div>
          ) : networkStatusUnavailable ? (
            <div className="device-overview__inline-muted">
              Live network status is not available for this firmware/API version.
            </div>
          ) : networkStatusError ? (
            <div className="device-overview__inline-error">{networkStatusError}</div>
          ) : networkStatusLoading && !networkStatus ? (
            <div className="device-overview__stack">
              <SkeletonLine width="100%" />
              <SkeletonLine width="75%" />
              <SkeletonLine width="86%" />
            </div>
          ) : networkInterfaceRows.length === 0 ? (
            <div className="device-overview__inline-muted">
              No network interfaces reported.
            </div>
          ) : (
            <div className="device-overview__table-wrap">
              <div className="device-overview__table-head device-overview__table-head--network">
                <div>Connection</div>
                <div>Role</div>
                <div>Status</div>
                <div>Details</div>
              </div>
              {networkInterfaceRows.map((item, index) => {
                const health = item?.health || "-";
                const tone = getNetworkHealthTone(health);
                const detail = getOverviewNetworkDetail(item);
                return (
                  <div
                    key={`${item?.interfaceName || "iface"}-${index}`}
                    className="device-overview__table-row device-overview__table-row--network"
                  >
                    <div>
                      {item?.label || item?.interfaceName || "-"}
                      {item?.active ? (
                        <span className="device-overview__inline-chip">
                          Active
                        </span>
                      ) : null}
                    </div>
                    <div>{item?.role || "-"}</div>
                    <div>
                      <span
                        className={`device-overview__pill ${
                          tone === "success"
                            ? "device-overview__pill--success"
                            : tone === "danger"
                              ? "device-overview__pill--danger"
                              : "device-overview__pill--neutral"
                        }`}
                      >
                        {health}
                      </span>
                    </div>
                    <div>{detail}</div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {permissionsReady && canViewOutlets ? (
      <section className="device-overview__card">
        <div className="device-overview__card-header">
          <h3 className="device-overview__card-title">
            {canSwitchOutlets ? "Outlets Quick Control" : "Outlet Status"}
          </h3>
          <Link
            className="device-overview__sub-link"
            to={`/devices/connected/${serial}/outlets`}
          >
            View all outlets
          </Link>
        </div>

        {outletsLoading && topOutlets.length === 0 ? (
          <div className="device-overview__stack">
            <SkeletonLine width="100%" />
            <SkeletonLine width="100%" />
            <SkeletonLine width="100%" />
          </div>
        ) : topOutlets.length === 0 ? (
          <div className="device-overview__inline-muted">
            No outlets available for this device.
          </div>
        ) : (
          <div className="device-overview__table-wrap">
            <div className="device-overview__table-head">
              <div>Outlet</div>
              <div>Current</div>
              <div>State</div>
              {canSwitchOutlets ? <div>Operation</div> : null}
            </div>
            {topOutlets.map((outlet) => {
              const outletId = outlet?.outlet_id;
              const outletNumber = outlet?.pduLabeledOutletNum || outletId;
              const outletLabel =
                (outlet?.outletName || "").trim() || `Outlet ${outletNumber}`;
              const isBusy = Boolean(outletLoadingMap[outletId]);
              const isConnected = outlet?.connectionState === "Connected";
              const actionDisabled =
                isBusy || !isConnected || !canSwitchOutlets;
              const operationConfigs = getOutletOperationConfigs(outlet);

              const currentCell =
                typeof outlet?.currentRms === "number" ? (
                  <div
                    className="device-overview__outlet-current"
                    title={
                      outlet?.receptacle
                        ? `${outlet.receptacle} Receptacle`
                        : "Outlet receptacle"
                    }
                  >
                    <span className="device-overview__outlet-current-value">
                      {(outlet.currentRms || 0).toFixed(2)}
                    </span>
                    <span className="device-overview__outlet-current-unit">A</span>
                  </div>
                ) : (
                  "-"
                );

              const normalizedState = (outlet?.state || "Unknown").toUpperCase();
              const pendingLabel =
                outlet?.rebootStatus === "pendingOn"
                  ? "(Pending On)"
                  : outlet?.rebootStatus === "pendingOff"
                    ? "(Pending Off)"
                    : "";
              const stateToneClass =
                outlet?.state === "ON"
                  ? "device-overview__outlet-state-main--on"
                  : outlet?.state === "OFF"
                    ? "device-overview__outlet-state-main--off"
                    : "device-overview__outlet-state-main--unknown";
              const pendingToneClass =
                outlet?.rebootStatus === "pendingOn"
                  ? "device-overview__outlet-state-sub--on"
                  : outlet?.rebootStatus === "pendingOff"
                    ? "device-overview__outlet-state-sub--off"
                    : "";

              return (
                <div
                  key={`quick-outlet-${outletId}`}
                  className="device-overview__table-row"
                >
                  <div>
                    <Link
                      className="device-overview__log-link"
                      to={`/devices/connected/${serial}/outlets/${outletId}`}
                    >
                      {outletLabel}
                    </Link>
                    <NameSyncStatus resource={outlet} />
                  </div>
                  <div>{currentCell}</div>
                  <div>
                    <div className="device-overview__outlet-state">
                      <span
                        className={`device-overview__outlet-state-main ${stateToneClass}`}
                      >
                        {normalizedState}
                      </span>
                      {pendingLabel ? (
                        <span
                          className={`device-overview__outlet-state-sub ${pendingToneClass}`}
                        >
                          {pendingLabel}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {canSwitchOutlets ? (
                  <div>
                    <div className="device-overview__action-wrap">
                      {operationConfigs.map((config) => (
                        <button
                          key={`${outletId}-${config.key}`}
                          type="button"
                          className={`device-overview__operation-badge device-overview__operation-badge--${config.tone}`}
                          onClick={() =>
                            runOutletAction(outlet, config.nextState, config.actionLabel)
                          }
                          disabled={actionDisabled}
                          title={!isConnected ? "Device is offline" : config.title}
                          aria-busy={isBusy}
                        >
                          <i className="material-icons" aria-hidden="true">
                            {config.icon}
                          </i>
                          <span>{config.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>
      ) : null}

      <section className="device-overview__card">
        <div className="device-overview__card-header">
          <h3 className="device-overview__card-title">
            Diagnostics Quick Tools
          </h3>
          <div className="device-overview__row-actions">
            <Link
              className="device-overview__sub-link"
              to={`/devices/connected/${serial}/diagnostics`}
            >
              Open diagnostics
            </Link>
          </div>
        </div>

        <div className="device-overview__field-group">
          <label className="device-overview__label" htmlFor="overview-ping-host">
            Ping host from this PDU
          </label>
          <div className="device-overview__row-inline">
            <input
              id="overview-ping-host"
              className="device-overview__input"
              value={pingHost}
              onChange={(event) => setPingHost(event.target.value)}
              placeholder="8.8.8.8 or example.com"
              disabled={pingLoading}
            />
            <button
              type="button"
              className="button"
              style={{ marginBottom: "0px" }}
              onClick={handlePingSubmit}
              disabled={pingLoading}
            >
              {pingLoading ? "Pinging..." : "Ping"}
            </button>
          </div>
          {pingStatus ? (
            <div
              className={`device-overview__ping-status ${
                pingStatus === "success"
                  ? "device-overview__ping-status--success"
                  : "device-overview__ping-status--error"
              }`}
            >
              {pingMessage}
            </div>
          ) : null}
          {shellState?.showShellTab ? (
          <div className="device-overview__advanced-block">
            <div className="device-overview__advanced-label">Advanced access</div>
            <div
              className={`device-overview__advanced-row ${
                shellLinkAvailable ? "" : "device-overview__advanced-row--disabled"
              }`}
            >
              <div className="device-overview__advanced-main">
                <i className="material-icons device-overview__advanced-icon">
                  terminal
                </i>
                <div className="device-overview__advanced-copy">
                  <div className="device-overview__advanced-title">
                    Shell workspace
                  </div>
                  <div className="device-overview__advanced-description">
                    Run CLI diagnostics inside the full diagnostics workspace.
                  </div>
                </div>
              </div>
              {shellLinkAvailable ? (
                <Link
                  className="device-overview__advanced-link"
                  to={{
                    pathname: `/devices/connected/${serial}/diagnostics`,
                    hash: "#shell-workspace",
                  }}
                >
                  Open
                  <i
                    className="material-icons"
                    style={{ textDecoration: "none" }}
                  >
                    chevron_right
                  </i>
                </Link>
              ) : (
                <span className="device-overview__advanced-status">
                  {shellStatusText}
                </span>
              )}
            </div>
          </div>
          ) : null}
        </div>
      </section>

      {permissionsReady && (activityPermission || eventPermission) ? (
      <section className="device-overview__card">
          <div className="device-overview__card-header">
            <h3 className="device-overview__card-title">Recent Activity</h3>
            <div className="device-overview__row-actions">
              <button
                type="button"
                className="button secondary device-overview__refresh-button"
                onClick={() => setLogsRefreshKey((value) => value + 1)}
                disabled={logsLoading || !permissionsReady}
              >
                <i className="material-icons" aria-hidden="true">
                  refresh
                </i>
                <span>{logsLoading ? "Refreshing..." : "Refresh"}</span>
              </button>
            </div>
          </div>

        <>
            <AppToolbar
              variant="plain"
              className="device-overview__activity-toolbar"
            >
              <AppToolbarGrid className="device-overview__activity-toolbar-grid">
                <AppToolbarField label="Show">
                  <div
                    className="device-overview__activity-filter-list"
                    aria-label="Recent activity filters"
                  >
                    {visibleActivitySourceFilters.map((filter) => {
                      const active = activitySourceKey === filter.key;
                      return (
                        <button
                          key={filter.key}
                          type="button"
                          className={`device-overview__activity-filter ${
                            active
                              ? "device-overview__activity-filter--active"
                              : ""
                          }`}
                          aria-pressed={active}
                          onClick={() => {
                            setLogsPage(1);
                            setActivitySourceKey(filter.key);
                            if (filter.key === "alerts") {
                              setActivityTypeKey("all");
                            }
                          }}
                        >
                          <i className="material-icons" aria-hidden="true">
                            {filter.icon}
                          </i>
                          <span>{filter.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </AppToolbarField>

                <AppToolbarField label="Type">
                  <select
                    value={activityTypeKey}
                    onChange={(event) => {
                      setLogsPage(1);
                      setActivityTypeKey(event.target.value);
                    }}
                    className="device-overview__select"
                    aria-label="Activity type"
                    disabled={!activityPermission || activitySourceKey === "alerts"}
                  >
                    {ACTIVITY_TYPE_FILTERS.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </AppToolbarField>

                <AppToolbarField label="Time range">
                  <select
                    value={timeframeKey}
                    onChange={handleTimeframeChange}
                    className="device-overview__select"
                    aria-label="Activity time range"
                  >
                    {TIMEFRAME_OPTIONS.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {/* <div className="app-toolbar__meta device-overview__activity-summary">
                    <span>{timeframeLabel}</span>
                  </div> */}
                </AppToolbarField>
              </AppToolbarGrid>
            </AppToolbar>

            {timeframeKey === "custom" ? (
              <div className="device-overview__custom-range-wrap device-overview__custom-range-wrap--inline">
                <DatePicker
                  selected={customRange?.[0] || null}
                  onChange={(value) =>
                    setCustomRange((prev) => [value || null, prev?.[1] || null])
                  }
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  placeholderText="Start time"
                  className="device-overview__input"
                />
                <DatePicker
                  selected={customRange?.[1] || null}
                  onChange={(value) =>
                    setCustomRange((prev) => [prev?.[0] || null, value || null])
                  }
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  placeholderText="End time"
                  className="device-overview__input"
                />
                <button type="button" className="button" onClick={applyCustomRange}>
                  Apply
                </button>
                {customError ? (
                  <div className="device-overview__inline-error">{customError}</div>
                ) : null}
              </div>
            ) : null}

            {logsLoading ? (
              <div className="device-overview__activity-list">
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={`timeline-skeleton-${index}`}
                    className="device-overview__activity-row"
                  >
                    <div className="device-overview__activity-icon">
                      <SkeletonLine width="18px" height={18} />
                    </div>
                    <div className="device-overview__activity-main">
                      <SkeletonLine width="60%" />
                      <SkeletonLine width="30%" />
                    </div>
                    <SkeletonLine width="90px" />
                  </div>
                ))}
              </div>
            ) : displayedLogsRows.length === 0 ? (
              <div className="device-overview__inline-muted">
                No activity found for the selected filters.
              </div>
            ) : (
              <div className="device-overview__activity-list">
                {displayedLogsRows.map((row) => renderLogRow(row))}
              </div>
            )}

            {logsMeta?.totalPages > 1 ? (
              <div className="device-overview__pagination-wrap">
                <ReactPaginate
                  pageCount={logsMeta.totalPages || 1}
                  forcePage={(logsPage || 1) - 1}
                  onPageChange={({ selected }) => setLogsPage(selected + 1)}
                  pageClassName="page-item"
                  previousLabel="Previous"
                  nextLabel="Next"
                  containerClassName="react-paginate"
                  activeClassName="selected"
                />
              </div>
            ) : null}
        </>
      </section>
      ) : null}

      {deviceOffline ? (
        <div className="device-overview__offline-banner">
          {offlineReason ||
            "Device is offline. Displaying latest known information where available."}
        </div>
      ) : null}
    </div>
  );
};

export default DeviceOverview;
